/**
 * Single source of truth for "which vehicle unit is this driver on".
 *
 * The server keys vehicle-scoped work (the driver-app socket room, chat history via
 * getChatHistory, driver:sendMessage persistence) on the vehicle's UnitID. That value
 * previously got resolved independently in several places with slightly different
 * fallback chains, so they could disagree — the socket would join room "ACET998" while
 * the Messages component read `undefined` and refused to send ("Socket or vehicleNumber
 * not available"). Everything now funnels through here.
 *
 * Priority, most authoritative first:
 *  1. The vehicle assigned to the current shift (dispatchData.vehicleObject).
 *  2. The vehicle chosen/assigned at login (selectedVehicle, mirrored in localStorage).
 *     This is what covers drivers logged in WITHOUT an active shift or route — the case
 *     that produced the blank "Messages - Vehicle" title.
 *  3. The driver record's own unit field, last: it's a driver-level attribute that can
 *     hold a stale placeholder the server can't resolve to any vehicle.
 *
 * Vehicle objects arrive with several casings for the same field (routes/driver.js
 * formatCompleteVehicleObject emits UnitID, unitId and unitID), hence the spread of keys.
 */

function readJson(key) {
  try {
    return JSON.parse(localStorage.getItem(key)) || null;
  } catch {
    return null;
  }
}

/** First non-empty unit-ish field on a vehicle object. */
function unitFromVehicle(vehicle) {
  if (!vehicle || typeof vehicle !== 'object') return '';
  const candidates = [
    vehicle.UnitID,
    vehicle.unitID,
    vehicle.unitId,
    vehicle.UnitId,
    // Code is the vehicle's own identifier and is what the server falls back to when
    // resolving history (socketComm getChatHistory scans vehiclesById by Code).
    vehicle.Code,
    vehicle.code,
  ];
  for (const value of candidates) {
    const text = String(value === null || value === undefined ? '' : value).trim();
    if (text) return text;
  }
  return '';
}

/**
 * @param {object} [options]
 * @param {object} [options.userSettings]    Defaults to localStorage 'userSettings'.
 * @param {object} [options.selectedVehicle] Defaults to localStorage 'selectedVehicle'.
 * @returns {string} The unit id, or '' when genuinely unknown.
 */
export function resolveVehicleUnitId(options = {}) {
  const userSettings = options.userSettings || readJson('userSettings') || {};
  const selectedVehicle = options.selectedVehicle || readJson('selectedVehicle') || null;

  // 1. Shift-assigned vehicle.
  const shiftVehicle =
    userSettings?.dispatchData?.vehicleObject || userSettings?.vehicleObject || null;
  const fromShift = unitFromVehicle(shiftVehicle);
  if (fromShift) return fromShift;

  // 2. Vehicle selected/assigned at login.
  const fromSelected = unitFromVehicle(selectedVehicle);
  if (fromSelected) return fromSelected;

  // 3. Driver record fallback.
  const fromDriver = [userSettings.UnitID, userSettings.unitID, userSettings.unitId];
  for (const value of fromDriver) {
    const text = String(value === null || value === undefined ? '' : value).trim();
    if (text) return text;
  }

  return '';
}

export default resolveVehicleUnitId;
